import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import "./VoucherBooking.css";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  TableOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useTable } from "../Table/TableContext";
import Navbar from "../../../layouts/navbar";
import Footer from "../../../layouts/footer";

const { Text, Link, Title } = Typography;
const { Option } = Select;

const VoucherTableBooking = observer((props: any) => {
  const { table, fetchTable, onCurrentBooking } = useTable();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location: any = useLocation();

  // Get voucher group data from navigation state
  const [voucherGroup, setVoucherGroup] = useState<any>(null);
  const [selectedVouchers, setSelectedVouchers] = useState<any[]>([]);
  const [allVouchers, setAllVouchers] = useState<any[]>([]);

  // Table search states
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const [adultPax, setAdultPax] = useState(0);
  const [childPax, setChildPax] = useState(0);
  const [bookingDate, setBookingDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rooms, setRooms] = useState([]);

  // UI states
  const [openDatepicker, setOpenDatepicker] = useState(false);

  // Initialize from location state
  useEffect(() => {
    if (location.state?.voucherGroup) {
      const group = location.state.voucherGroup;
      setVoucherGroup(group);
      setAllVouchers(group.individual_vouchers || []);

      // Auto-select active vouchers
      const activeVouchers = group.individual_vouchers.filter((v: any) =>
        isVoucherActive(v)
      );
      setSelectedVouchers(activeVouchers);

      // Set pre-filled data
      setSelectedRestaurant({
        store_id: group.store_id,
        name: group.store_name,
      });

      // Set default date to today
      setBookingDate(moment().format("YYYY-MM-DD"));

      // Calculate initial pax from active vouchers
      updatePaxFromVouchers(activeVouchers);
    } else {
      // Redirect back if no voucher data
      message.error("No voucher data found. Please go back and try again.");
      navigate("/vouchers");
    }
  }, [location.state, navigate]);

  // Update the isVoucherActive function to match the new status logic
  const isVoucherActive = (voucher: any) => {
    const isUsed = voucher.status === "1" || voucher.usedate !== null;
    const isExpired = moment().isAfter(moment(voucher.valid_date));
    return voucher.status === "0" && !isUsed && !isExpired;
  };

  // Helper function to get disabled reason
  const getDisabledReason = (voucher: any) => {
    if (voucher.status === "1" || voucher.usedate !== null) {
      return "Voucher already used";
    } else if (voucher.status === "3") {
      return "Voucher already booked for table";
    } else if (moment().isAfter(moment(voucher.valid_date))) {
      return "Voucher expired";
    }
    return "Cannot be used for booking";
  };

  // Update the getVoucherStatus function to match the new status logic
  const getVoucherStatus = (voucher: any) => {
    const isUsed = voucher.status === "1" || voucher.usedate !== null;
    const isTableBooked = voucher.status === "3";
    const isExpired = moment().isAfter(moment(voucher.valid_date));

    if (isTableBooked) {
      return {
        color: "blue",
        text: "Table Booked",
        icon: (
          <TableOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      };
    } else if (isUsed) {
      return {
        color: "red",
        text: "Used",
        icon: (
          <CheckCircleOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      };
    } else if (isExpired) {
      return {
        color: "orange",
        text: "Expired",
        icon: (
          <CloseCircleOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      };
    } else {
      return {
        color: "green",
        text: "Active",
        icon: (
          <CheckCircleOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        ),
      };
    }
  };

  // Add a helper function to check if voucher can be selected for booking
  const canVoucherBeSelected = (voucher: any) => {
    return (
      voucher.status === "0" &&
      !voucher.usedate &&
      moment().isBefore(moment(voucher.valid_date))
    );
  };

  // Update pax counts based on selected vouchers
  const updatePaxFromVouchers = (vouchers: any[]) => {
    let adults = 0;
    let children = 0;

    vouchers.forEach((voucher) => {
      if (voucher.price_title?.toLowerCase().includes("adult")) {
        adults += 1;
      } else if (voucher.price_title?.toLowerCase().includes("child")) {
        children += 1;
      }
      // Add more types if needed (senior, etc.)
    });

    setAdultPax(adults);
    setChildPax(children);
  };

  // Handle voucher selection/deselection
  const handleVoucherSelection = (voucher: any, selected: boolean) => {
    let newSelectedVouchers;

    if (selected) {
      // Add voucher to selection
      newSelectedVouchers = [...selectedVouchers, voucher];
    } else {
      // Remove voucher from selection
      newSelectedVouchers = selectedVouchers.filter(
        (v) => v.voucher_code !== voucher.voucher_code
      );
    }

    setSelectedVouchers(newSelectedVouchers);
    updatePaxFromVouchers(newSelectedVouchers);
  };

  // Select/deselect all active vouchers
  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const selectableVouchers = allVouchers.filter((v) =>
        canVoucherBeSelected(v)
      );
      setSelectedVouchers(selectableVouchers);
      updatePaxFromVouchers(selectableVouchers);
    } else {
      setSelectedVouchers([]);
      setAdultPax(0);
      setChildPax(0);
    }
  };

  // Check if all selectable vouchers are selected
  const areAllSelectableSelected = () => {
    const selectableVouchers = allVouchers.filter((v) =>
      canVoucherBeSelected(v)
    );
    return (
      selectableVouchers.length > 0 &&
      selectedVouchers.length === selectableVouchers.length
    );
  };

  // Handle table search
  const onSearch = () => {
    if (!bookingDate) {
      message.error("Please select a booking date");
      return;
    }

    if (selectedVouchers.length === 0) {
      message.error("Please select at least one voucher to book table");
      return;
    }

    setSearching(true);
    setHasSearched(true); // Mark that search was performed

    // Prepare voucher codes for API
    const voucherCodes = selectedVouchers.map((v) => v.voucher_code).join(",");

    fetchTable({
      name: selectedRestaurant?.name,
      date: bookingDate,
      adultPax: adultPax,
      childPax: childPax,
      voucher_code: voucherGroup.orderid,
    })
      .then((response: any) => {
        setSearching(false);
        if (response.timeSlots && response.timeSlots.length > 0) {
          message.success(
            `Found ${response.timeSlots.length} available time slots!`
          );
        } else {
          message.info(
            "No available time slots found for the selected criteria"
          );
        }
      })
      .catch((error: any) => {
        setSearching(false);
        message.error("Failed to search for tables. Please try again.");
        console.error("Table search error:", error);
      });
  };

  // Handle table selection and proceed to checkout
  const onSelectTable = (timeSlot: any) => {
    if (selectedVouchers.length === 0) {
      message.error("Please select vouchers before proceeding");
      return;
    }

    // Show confirmation modal
    Modal.confirm({
      title: "Confirm Table Booking",
      content: (
        <Space direction="vertical">
          <Text>Are you sure you want to book:</Text>
          <Text strong>{table.name}</Text>
          <Text>
            Date:{" "}
            <Text strong>{moment(bookingDate).format("DD MMM YYYY")}</Text>
          </Text>
          <Text>
            Time: <Text strong>{timeSlot.time_slot}</Text>
          </Text>
          <Text>
            Party: <Text strong>{adultPax} Adults</Text>
            {childPax > 0 && (
              <>
                , <Text strong>{childPax} Children</Text>
              </>
            )}
          </Text>
          {/* <Text>
            Deposit: <Text strong>RM {timeSlot.Deposit}</Text>
          </Text> */}
          <Text>
            Vouchers: <Text strong>{selectedVouchers.length} selected</Text>
          </Text>
        </Space>
      ),
      okText: "Yes, Proceed to Checkout",
      cancelText: "Cancel",
      onOk() {
        // Prepare booking data for VoucherTableCheckout
        const bookingData = {
          selectedProduct: table,
          bookingParams: {
            store_id: selectedRestaurant?.store_id,
            store_name: table.name,
            datereserve: bookingDate,
            timereserve: timeSlot,
            number_adult: adultPax,
            number_child: childPax,
            name: user?.firstname
              ? `${user.firstname} ${user.lastname || ""}`.trim()
              : "",
            phone: user?.phone || "",
            email: user?.email || "",
            selected_vouchers: selectedVouchers,
            voucher_ids: selectedVouchers.map((v) => v.voucher_code), // Array for API
            deposit_amount: timeSlot.Deposit,
          },
        };

        console.log("Voucher booking data:", bookingData);
        onCurrentBooking(bookingData);

        // Navigate to VOUCHER table checkout
        navigate(`/voucher/${voucherGroup.orderid}/assign-slot/checkout`, {
          state: {
            voucherGroup: voucherGroup,
            selectedVouchers: selectedVouchers,
            bookingData: bookingData,
          },
        });
      },
    });
  };

  // Add day mapping for calculations
  const dayMap: any = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Function to find the nearest available date
  const findNearestAvailableDate = (availableDays: any) => {
    if (!availableDays || availableDays.length === 0) {
      return moment().add(1, "day"); // Default to tomorrow
    }

    const today = moment();
    const currentDay = today.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Convert available day names to day numbers
    const availableDayNumbers = availableDays.map(
      (day: any) => dayMap[day.toLowerCase()]
    );

    // Sort available days by their number
    availableDayNumbers.sort((a: any, b: any) => a - b);

    // Find the next available day in the current week
    let nearestDay = availableDayNumbers.find((day: any) => day > currentDay);

    if (nearestDay !== undefined) {
      // Available day found in the current week
      return today.day(nearestDay);
    } else {
      // No available days in current week, get first available day of next week
      const daysUntilNextWeek = 7 - currentDay + availableDayNumbers[0];
      return today.add(daysUntilNextWeek, "days");
    }
  };

  // Auto-select nearest available date when selectedVouchers changes
  useEffect(() => {
    const available_days = selectedVouchers[0]?.available_days;
    if (available_days && available_days.length > 0 && !bookingDate) {
      const nearestDate = findNearestAvailableDate(available_days);
      setBookingDate(nearestDate.format("YYYY-MM-DD"));

      // Optional: Show info message
      console.log(`Auto-selected ${nearestDate.format("dddd, MMMM Do")}`);
    }
  }, [selectedVouchers, bookingDate]);

  // Update your onChangeDate function to handle invalid selections
  const onChangeDate = (date: any, dateString: string) => {
    if (date) {
      const available_days = selectedVouchers[0]?.available_days;
      const selectedDay = date.format("dddd").toLowerCase();

      if (available_days && !available_days.includes(selectedDay)) {
        message.error(
          `This voucher is only available on: ${available_days.join(", ")}`
        );
        // Auto-select the nearest available date
        const nearestDate = findNearestAvailableDate(available_days);
        setBookingDate(nearestDate.format("YYYY-MM-DD"));
        message.info(`Auto-selected ${nearestDate.format("dddd, MMMM Do")}`);
        return;
      }

      setBookingDate(dateString);
    } else {
      setBookingDate("");
    }
  };

  const onOpenChange = () => {
    setOpenDatepicker(!openDatepicker);
  };

  if (!voucherGroup) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text>Loading voucher data...</Text>
        </div>
      </div>
    );
  }

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar hideBanner={true} />
        <div style={{ marginBottom: "24px" }}>
          {/* <Button
            type="link"
            onClick={() => navigate("/bookings/voucher")}
            style={{ paddingLeft: 0 }}
          >
            ← Back
          </Button> */}
          <Title level={3}>Book Table with Vouchers</Title>
          <Text type="secondary">
            Using vouchers from order:{" "}
            <Text strong>#{voucherGroup.orderid}</Text>
          </Text>
        </div>

        {/* Voucher Selection Section */}
        <Card
          title="Select Vouchers for Table Booking"
          style={{ marginBottom: "24px" }}
          extra={
            <Checkbox
              checked={areAllSelectableSelected()}
              indeterminate={
                selectedVouchers.length > 0 &&
                selectedVouchers.length <
                  allVouchers.filter((v) => canVoucherBeSelected(v)).length
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Select All Available
            </Checkbox>
          }
        >
          <div style={{ marginBottom: "16px" }}>
            <Alert
              message={
                <Space direction="vertical" size="small">
                  <Text strong>Booking Summary</Text>
                  <Text>
                    Selected: <Text strong>{selectedVouchers.length}</Text> of{" "}
                    <Text strong>
                      {
                        allVouchers.filter((v) => canVoucherBeSelected(v))
                          .length
                      }
                    </Text>{" "}
                    available vouchers
                  </Text>
                  <Text>
                    Total Pax: <Text strong>{adultPax} Adults</Text>
                    {childPax > 0 && (
                      <>
                        , <Text strong>{childPax} Children</Text>
                      </>
                    )}
                  </Text>
                </Space>
              }
              type="info"
              showIcon
            />
          </div>

          <List
            dataSource={allVouchers}
            renderItem={(voucher: any) => {
              const status = getVoucherStatus(voucher);
              const isSelectable = canVoucherBeSelected(voucher);
              const isSelected = selectedVouchers.some(
                (v) => v.voucher_code === voucher.voucher_code
              );

              return (
                <List.Item>
                  <Space style={{ width: "100%" }} align="start">
                    <Checkbox
                      checked={isSelected}
                      disabled={!isSelectable}
                      onChange={(e) =>
                        handleVoucherSelection(voucher, e.target.checked)
                      }
                    />

                    <div style={{ flex: 1 }}>
                      <Space>
                        <Text strong>{voucher.price_title}</Text>
                        <Tag color={status.color} icon={status.icon}>
                          {status.text}
                        </Tag>
                        <Text code>{voucher.voucher_code}</Text>
                      </Space>
                      <div>
                        <Text type="secondary">
                          Amount: RM{" "}
                          {parseFloat(voucher.amount || 0).toFixed(2)}
                        </Text>
                        {!isSelectable && (
                          <Text type="secondary" style={{ marginLeft: "8px" }}>
                            • {getDisabledReason(voucher)}
                          </Text>
                        )}
                      </div>
                    </div>
                  </Space>
                </List.Item>
              );
            }}
          />
        </Card>

        {/* Table Search Section */}
        <Card title="Table Booking Details" style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Text strong>Restaurant</Text>
              </div>
              <Input
                value={selectedRestaurant?.name || ""}
                disabled
                style={{ width: "100%" }}
                placeholder="Restaurant from voucher"
              />
            </Col>

            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Text strong>Booking Date</Text>
              </div>
              <DatePicker
                open={openDatepicker}
                onOpenChange={onOpenChange}
                size="large"
                placeholder="Select date"
                onChange={onChangeDate}
                value={bookingDate ? moment(bookingDate) : null}
                disabledDate={(current) => {
                  const available_days = selectedVouchers[0]?.available_days;
                  // Disable past dates
                  if (moment().add(-1, "days") >= current) {
                    return true;
                  }

                  // If no available_days data, allow all future dates
                  if (!available_days || available_days.length === 0) {
                    return false;
                  }

                  // Get the day name in lowercase (e.g., "monday", "tuesday")
                  const dayName = current.format("dddd").toLowerCase();

                  // Disable if the day is NOT in available_days array
                  return !available_days.includes(dayName);
                }}
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Text strong>Party Size (Auto-calculated)</Text>
              </div>
              <Input
                value={`${adultPax} Adults, ${childPax} Children`}
                disabled
                style={{ width: "100%" }}
                placeholder="Based on selected vouchers"
              />
            </Col>
          </Row>

          <div style={{ marginTop: "16px", textAlign: "center" }}>
            <Button
              type="primary"
              icon={
                <SearchOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
              loading={searching}
              onClick={onSearch}
              disabled={selectedVouchers.length === 0 || !bookingDate}
              size="large"
              style={{
                backgroundColor: "#e90056",
                borderColor: "#e90056",
                minWidth: "200px",
              }}
            >
              Search Available Tables
            </Button>
          </div>
        </Card>

        {/* Available Time Slots */}
        {rooms.length > 0 && (
          <Card title="Available Time Slots">
            <div style={{ marginBottom: "16px" }}>
              <Text type="secondary">
                Found {table.timeSlots?.length || 0} available time slots for
                your party
              </Text>
            </div>

            <Row gutter={[8, 8]}>
              {/* Available Time Slots */}
              {table.timeSlots && table.timeSlots.length > 0 && (
                <Card title="Available Time Slots">
                  <div style={{ marginBottom: "16px" }}>
                    <Alert
                      message={
                        <Space direction="vertical" size="small">
                          <Text strong>Restaurant Information</Text>
                          <Text>
                            {/* @ts-ignore */}
                            {table.name} • {table.address}
                          </Text>
                          {/* @ts-ignore */}
                          <Text>Tel: {table.tel}</Text>
                        </Space>
                      }
                      type="info"
                      showIcon
                    />
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <Text type="secondary">
                      Found <Text strong>{table.timeSlots.length}</Text>{" "}
                      available time slots for your party of{" "}
                      <Text strong>{adultPax} Adults</Text>
                      {childPax > 0 && (
                        <>
                          , <Text strong>{childPax} Children</Text>
                        </>
                      )}
                    </Text>
                  </div>

                  <Row gutter={[8, 8]}>
                    {table.timeSlots.map((slot: any, index: number) => (
                      <Col key={index} xs={12} sm={8} md={6} lg={4}>
                        <Card
                          size="small"
                          onClick={() => onSelectTable(slot)}
                          style={{
                            cursor: "pointer",
                            border: "1px solid #d9d9d9",
                            borderRadius: "8px",
                            textAlign: "center",
                            transition: "all 0.3s ease",
                          }}
                          bodyStyle={{ padding: "12px 8px" }}
                          hoverable
                        >
                          <div style={{ marginBottom: "8px" }}>
                            <Text strong style={{ fontSize: "16px" }}>
                              {slot.time_slot}
                            </Text>
                          </div>
                          <div style={{ marginBottom: "4px" }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              Tables Left: {slot.Table_Left}
                            </Text>
                          </div>
                          {/* <div>
                            <Tag color="blue" style={{ margin: 0 }}>
                              Deposit: RM {slot.Deposit}
                            </Tag>
                          </div> */}
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <div style={{ marginTop: "16px", textAlign: "center" }}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      💡 Click on a time slot to proceed with your booking
                    </Text>
                  </div>
                </Card>
              )}
            </Row>
          </Card>
        )}
        {/* Available Time Slots */}
        {table.timeSlots && table.timeSlots.length > 0 ? (
          <Card title="Available Time Slots">
            <div style={{ marginBottom: "16px" }}>
              <Alert
                message={
                  <Space direction="vertical" size="small">
                    <Text strong>Restaurant Information</Text>
                    <Text>
                      {/* @ts-ignore */}
                      {table.name} • {table.address}
                    </Text>
                    {/* @ts-ignore */}
                    <Text>Tel: {table.tel}</Text>
                  </Space>
                }
                type="info"
                showIcon
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <Text type="secondary">
                Found <Text strong>{table.timeSlots.length}</Text> available
                time slots for your party of{" "}
                <Text strong>{adultPax} Adults</Text>
                {childPax > 0 && (
                  <>
                    , <Text strong>{childPax} Children</Text>
                  </>
                )}
              </Text>
            </div>

            <Row gutter={[8, 8]}>
              {table.timeSlots.map((slot: any, index: number) => (
                <Col key={index} xs={12} sm={8} md={6} lg={4}>
                  <Card
                    size="small"
                    onClick={() => onSelectTable(slot)}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #d9d9d9",
                      borderRadius: "8px",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                    }}
                    bodyStyle={{ padding: "12px 8px" }}
                    hoverable
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <Text strong style={{ fontSize: "16px" }}>
                        {slot.time_slot}
                      </Text>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Tables Left: {slot.Table_Left}
                      </Text>
                    </div>
                    {/* <div>
                      <Tag color="blue" style={{ margin: 0 }}>
                        Deposit: RM {slot.Deposit}
                      </Tag>
                    </div> */}
                  </Card>
                </Col>
              ))}
            </Row>

            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                💡 Click on a time slot to proceed with your booking
              </Text>
            </div>
          </Card>
        ) : (
          <Card title="Ready to Search">
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text>
                Click "Search Available Tables" to find available time slots at{" "}
                <Text strong>{selectedRestaurant?.name}</Text>
              </Text>
            </div>
          </Card>
        )}

        {searching && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              <Text>
                Searching for available tables at {selectedRestaurant?.name}...
              </Text>
            </div>
          </div>
        )}

        {!searching &&
          hasSearched &&
          (!table.timeSlots || table.timeSlots.length === 0) && (
            <Card title="No Tables Available">
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Text type="secondary">
                  No available time slots found for {selectedRestaurant?.name}{" "}
                  on {moment(bookingDate).format("DD MMM YYYY")}.
                </Text>
                <br />
                <Text type="secondary">
                  Please try a different date or contact the restaurant
                  directly.
                </Text>
                <div style={{ marginTop: "16px" }}>
                  <Button
                    type="primary"
                    onClick={onSearch}
                    style={{
                      backgroundColor: "#e90056",
                      borderColor: "#e90056",
                    }}
                  >
                    Search Again
                  </Button>
                </div>
              </div>
            </Card>
          )}

        {/* // {!searching &&
        //   !hasSearched &&
        //   selectedVouchers.length > 0 &&
        //   bookingDate && (
        //     <Card title="Ready to Search">
        //       <div style={{ textAlign: "center", padding: "40px" }}>
        //         <Text>
        //           Click "Search Available Tables" to find available time slots
        //           at <Text strong>{selectedRestaurant?.name}</Text>
        //         </Text>
        //       </div>
        //     </Card>
        //   )} */}
        <Footer />
      </div>
    </div>
  );
});

export default VoucherTableBooking;
